import { cn } from '@/lib/utils';
import { formatarInteiro } from '@/features/relatorios/formato';

export interface ItemBarra {
  chave: string;
  rotulo: string;
  valor: number;
  /** Informação secundária à direita do valor, como a taxa correspondente. */
  detalhe?: string;
  /** Classe de fundo da barra. Padrão: a cor da marca. */
  cor?: string;
}

interface Props {
  itens: ItemBarra[];
  /** Texto exibido quando não há nada a mostrar. */
  vazio?: string;
  /** Formatação do número à direita. Padrão: inteiro com separador de milhar. */
  formatar?: (valor: number) => string;
  className?: string;
}

/**
 * Ranking em barras horizontais.
 *
 * Barra é enfeite: o número ao lado carrega o dado, e a lista continua legível
 * impressa em preto e branco ou lida por leitor de tela. Por isso a barra é
 * `aria-hidden` e a proporção nunca é a única forma de comparar.
 *
 * A escala é relativa ao maior item da lista, não ao total — a pergunta destes
 * blocos é "quem lidera e por quanto", não "que fatia do bolo".
 */
export function GraficoBarras({
  itens,
  vazio = 'Sem dados no período.',
  formatar = formatarInteiro,
  className,
}: Props) {
  if (itens.length === 0) {
    return <p className="text-silenciado-texto py-6 text-center text-sm">{vazio}</p>;
  }

  const maior = Math.max(...itens.map((item) => item.valor), 1);

  return (
    <ul className={cn('flex flex-col gap-2.5', className)}>
      {itens.map((item) => (
        <li key={item.chave}>
          <div className="mb-1 flex items-baseline justify-between gap-3 text-sm">
            <span className="min-w-0 truncate" title={item.rotulo}>
              {item.rotulo}
            </span>
            <span className="flex shrink-0 items-baseline gap-2">
              {item.detalhe && (
                <span className="text-silenciado-texto text-xs">{item.detalhe}</span>
              )}
              <span className="font-semibold tabular-nums">{formatar(item.valor)}</span>
            </span>
          </div>
          <div className="bg-silenciado h-1.5 overflow-hidden rounded-full">
            <div
              className={cn('h-full rounded-full', item.cor ?? 'bg-marca')}
              // Largura mínima para que um item com valor 1 ao lado de outro
              // com 900 não desapareça e pareça zero.
              style={{
                width: `${Math.max((item.valor / maior) * 100, item.valor > 0 ? 1.5 : 0)}%`,
              }}
              aria-hidden="true"
            />
          </div>
        </li>
      ))}
    </ul>
  );
}
